import { useEffect, useRef, useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useScreenTrail } from '../../lib/sessionBack'
import type { ScreenKey } from '../../lib/screenIds'
import type { SessionMessage, SessionMode } from '../../types/session'
import { callClaude } from '../../lib/claude'
import { errorCodeOf, guideErrorKey } from '../../lib/guideErrors'
import { parseOptions } from '../../lib/parseOptions'
import { detectCrisis } from '../../lib/crisis'
import { ageAnswer } from '../../lib/age'
import { useVoiceInput } from '../../hooks/useVoiceInput'
import LoadingSpiral from '../ui/LoadingSpiral'
import IntuitiveChoice from '../ui/IntuitiveChoice'
import type { ChoiceState } from '../ui/IntuitiveChoice'
import CrisisAlert from '../ui/CrisisAlert'
import { useTranslation } from 'react-i18next'
import { localizeEmotionLabel } from '../../data/emotions.pl'

// Fallback options for the intuitive feeling picker (Phase 20 generates tailored options)
const FEELING_OPTIONS_FALLBACK = [
  'Anger / Frustration',
  'Sadness / Grief',
  'Fear / Anxiety',
  'Shame / Guilt',
  'Overwhelm',
  'Numbness / Emptiness',
  'Betrayal / Hurt',
  'Loneliness',
]

// Fallback beliefs for the Meaning Imprint step (Phase 20 generates tailored options)
const BELIEF_OPTIONS_FALLBACK = [
  "I'm not enough",
  "I have to earn my place",
  "I'm too much for people",
  "I can't trust anyone",
  "I have to do it alone",
  "Something is wrong with me",
  "I don't deserve good things",
  "I have to hold it all together",
  "I am too broken to heal",
]

// Somatic sensation chips — intentionally static (anatomical descriptors, not interpretations)
const SOMATIC_CHIPS = [
  'It expands',
  'It tightens',
  'It moves',
  'It stays the same',
  'It pulses',
  'It softens',
  'It radiates',
  'It aches',
  'It trembles',
]

// Chakra body locations — intentionally static (chakra-mapped with specific colors/symbolism)
const CHAKRA_LOCATIONS = [
  { name: 'Head',           color: '#845EC2', bg: '#F3EEFF' },  // Crown — violet
  { name: 'Throat',         color: '#0081CF', bg: '#E8F4FF' },  // Throat — blue
  { name: 'Chest',          color: '#00897B', bg: '#E6F5F3' },  // Heart — teal-green
  { name: 'Heart',          color: '#2E7D32', bg: '#E8F5E9' },  // Heart — green
  { name: 'Solar plexus',   color: '#F9A825', bg: '#FFF8E1' },  // Solar Plexus — yellow
  { name: 'Stomach',        color: '#EF6C00', bg: '#FFF3E0' },  // Sacral — orange
  { name: 'Lower abdomen',  color: '#E64A19', bg: '#FBE9E7' },  // Sacral — deep orange
  { name: 'Lower back',     color: '#C62828', bg: '#FFEBEE' },  // Root — red
  { name: 'Legs',           color: '#B71C1C', bg: '#FFEBEE' },  // Root — deep red
]

interface FeelProps {
  sessionId: string
  emotion: string
  intensity: number
  storyText: string
  firstName?: string
  lifePathNumber?: number
  seriesContext?: string
  /**
   * Mind-level lens chosen at Arrival. When 'rain', the entire phase 2 flow
   * is replaced with the RAIN sequence (Recognize → Allow → Investigate → Nurture).
   */
  sessionMode?: SessionMode
  /**
   * Updated signature: now passes beliefText (Meaning Imprint answer),
   * expressionText (Expression Step answer) and ageText (the optional answer
   * under the reflection, '' if none) to SessionPage.
   */
  onComplete: (bodyLocation: string, reminderText: string, beliefText: string, expressionText: string, ageText: string) => void
}

type UIState =
  | 'intuitive-feeling'  // Screen 1 — pick your feeling
  | 'opening'            // Fetching Phase 2 question
  | 'awaiting-input'     // Screen 2 — body location + early memory
  | 'somatic'            // Somatic Activation — "what changes as you breathe in?"
  | 'meaning-input'      // Meaning Imprint — "what did this make you believe about yourself?"
  | 'expression'         // Expression Step — uncensored one thing
  | 'reflected'          // Claude's reflection (result of background Phase 2 call)
  // ── RAIN lens states (only used when sessionMode === 'rain') ──
  | 'rain-recognize'     // RAIN-R — "Where do you feel this?" (static opener)
  | 'rain-allow'         // RAIN-A — Claude responds with allow prompt
  | 'rain-investigate'   // RAIN-I — Claude responds with investigation prompt
  | 'rain-nurture'       // RAIN-N — Claude offers nurturing reflection
  | 'error'

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<UIState, ScreenKey> = {
  'intuitive-feeling': 'feel.feeling',
  'opening':           'feel.body',
  'awaiting-input':    'feel.body',
  'somatic':           'feel.somatic',
  'meaning-input':     'feel.belief',
  'expression':        'feel.expression',
  'reflected':         'feel.reflection',
  'rain-recognize':    'feel.rain.recognize',
  'rain-allow':        'feel.rain.allow',
  'rain-investigate':  'feel.rain.investigate',
  'rain-nurture':      'feel.rain.nurture',
  'error':             'feel.error',
}

// RAIN step labels and the Recognize opener now live in the locale
// (feel.rainR/A/I/N, feel.rainOpening) and are read inside the component.

export default function Feel({
  sessionId, emotion, intensity, storyText,
  firstName = '', lifePathNumber, seriesContext, sessionMode, onComplete,
}: FeelProps) {
  const { t, i18n } = useTranslation()

  /*
   * The chip sets, body locations and RAIN labels are module-level constants,
   * so they can never be translated where they sit. They are read from the
   * locale here instead.
   *
   * Deliberately named *List / *Fallback rather than reusing the option names:
   * feelingOptions, beliefOptions and somaticChips are already useState values
   * holding the API-generated set, and shadowing those would silently swap
   * personalised options for the static ones.
   *
   * A locale array is only trusted at the exact expected length. A short or
   * missing one falls back to the full English set rather than rendering a
   * half-empty picker.
   */
  const localArray = (key: string, fallback: string[]) => {
    const v = t(key, { returnObjects: true })
    return Array.isArray(v) && v.length === fallback.length ? (v as string[]) : fallback
  }
  const feelingFallback = localArray('feel.feelingOptions', FEELING_OPTIONS_FALLBACK)
  const beliefFallback = localArray('feel.beliefOptions', BELIEF_OPTIONS_FALLBACK)
  const somaticChipList = localArray('feel.somaticChips', SOMATIC_CHIPS)
  // Each entry is "Place — meaning"; the list below splits on the em dash so
  // the place stays bold without markup living in the locale file.
  const bodyHelperList = localArray('feel.bodyHelperList', [
    'Chest / Heart — grief, sadness, longing, love',
    'Throat — things unsaid, fear of speaking, grief',
    'Stomach / Gut — anxiety, dread, disgust, intuition',
    'Solar plexus — anger, powerlessness, shame',
    'Shoulders / Neck — tension, burden, responsibility',
    'Jaw / Face — held-back anger, control',
    'Lower back — fear, survival stress',
    'Legs — feeling ungrounded, fight-or-flight',
  ])
  // Merged by position: only `name` is language-dependent — the colours carry
  // the chakra mapping and must not move.
  const chakraNames = localArray('feel.bodyLocations', CHAKRA_LOCATIONS.map(l => l.name))
  const chakraList = CHAKRA_LOCATIONS.map((loc, i) => ({ ...loc, name: chakraNames[i] ?? loc.name }))
  const rainLabels: Record<'R' | 'A' | 'I' | 'N', string> = {
    R: t('feel.rainR'), A: t('feel.rainA'), I: t('feel.rainI'), N: t('feel.rainN'),
  }
  const rainOpeningText = t('feel.rainOpening')

  // RAIN mode jumps straight into the somatic recognize step — bypasses the
  // standard intuitive-feeling picker entirely.
  const [uiState, setUiState] = useState<UIState>(
    sessionMode === 'rain' ? 'rain-recognize' : 'intuitive-feeling'
  )
  useScreenId(SCREEN_OF[uiState])

  // Intuitive feeling
  const [intuitiveFeeling, setIntuitiveFeeling] = useState('')
  const [feelingOptions, setFeelingOptions] = useState<string[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)

  // Phase 2 — body / memory
  const [claudeQuestion, setClaudeQuestion] = useState<string | null>(null)
  const [claudeReflection, setClaudeReflection] = useState<string | null>(null)
  const [bodyLocation, setBodyLocation] = useState('')
  const [reminderText, setReminderText] = useState('')
  const [noMemory, setNoMemory] = useState(false)

  // Somatic Activation
  const [somaticChips, setSomaticChips] = useState<string[]>([])
  const [somaticNote, setSomaticNote] = useState('')

  // Body reference helper toggle
  const [showBodyRef, setShowBodyRef] = useState(false)

  // Meaning Imprint
  const [beliefText, setBeliefText] = useState('')
  const [beliefOptions, setBeliefOptions] = useState<string[]>([])
  const [beliefOptionsLoading, setBeliefOptionsLoading] = useState(true)

  // Expression Step
  const [expressionText, setExpressionText] = useState('')
  const [showCrisis, setShowCrisis] = useState(false)

  // ─── RAIN state (only used when sessionMode === 'rain') ──────────────────
  // rainInput  — user's textarea input for the current RAIN step
  // rainClaudeText — Claude's question/reflection for the current step (null on R)
  // rainLoading — true while fetching Claude's next-step prompt
  // rainResponses — accumulated user answers, keyed by step letter (R/A/I/N).
  //   Used to populate the legacy onComplete fields:
  //     bodyLocation ← rainResponses.R (where they feel it — replaces standard body picker)
  //     reminderText ← rainResponses.I (what it needs/protects — closest analog to "memory")
  //     beliefText   ← rainResponses.A (allow response — the felt experience)
  //     expressionText ← rainResponses.I (investigate — also rich expression)
  const [rainInput, setRainInput] = useState('')
  const [rainClaudeText, setRainClaudeText] = useState<string | null>(null)
  const [rainLoading, setRainLoading] = useState(false)
  const [rainResponses, setRainResponses] = useState<Record<string, string>>({})

  // The optional answer under the reflection, as typed (so Back shows it as it was
  // left), and whether it has set off the crisis alert.
  const [ageText, setAgeText] = useState('')
  const [ageCrisis, setAgeCrisis] = useState(false)

  // Why the last call failed, as a code the error screen translates (lib/claude.ts).
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // What the guide has written for each RAIN step after Recognize, keyed by the
  // step it introduces. Kept so that going back to a step and continuing with
  // the same answer shows the step that is already written instead of asking
  // the guide for it again.
  // What the person chose on 2.1 and 2.4. Kept here, not in the picker, so it
  // outlives the picker: Back to this screen shows the same chips and words, and
  // Continue with nothing touched hands on the same answer (#68).
  const [feelingPick, setFeelingPick] = useState<ChoiceState | undefined>()
  const [beliefPick, setBeliefPick] = useState<ChoiceState | undefined>()

  const [rainTexts, setRainTexts] = useState<Record<string, string>>({})

  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const voice = useVoiceInput()

  // What the body/memory answers were when the current reflection (and the
  // belief options) were asked for. Going back to those screens and continuing
  // with the same words must not ask again; different words must.
  const reflectionFor = useRef<string | null>(null)
  const beliefOptionsFor = useRef<string | null>(null)

  // The age question is offered only to someone it is safe to offer it to: the
  // standard lens, not someone who has just said nothing comes to mind for an early
  // memory (they have declined that door once), and not at the highest intensities.
  // The cut-off is the reviewing therapist's judgment, not a measured figure.
  const ageShown = (sessionMode ?? 'standard') === 'standard' && !noMemory && intensity < 8

  const somaticHasInput = somaticChips.length > 0 || somaticNote.trim().length > 0

  // ─── Back ──────────────────────────────────────────────────────────────────
  // The screens Feel has shown, in order (lib/sessionBack.ts). RAIN's four
  // steps share one text box and one question slot, so each RAIN screen is
  // captured with its own and put back with them.
  useScreenTrail<UIState, { input: string; text: string | null } | undefined>({
    screen: uiState,
    go: setUiState,
    kind: s =>
      s === 'opening' ? 'loading'
        : s === 'error' ? 'error'
        : s === 'reflected' && claudeReflection === null ? 'loading'
        : s.startsWith('rain-') && rainLoading ? 'loading'
        : 'rest',
    capture: () => (sessionMode === 'rain' ? { input: rainInput, text: rainClaudeText } : undefined),
    restore: slot => {
      if (!slot) return
      setRainInput(slot.input)
      setRainClaudeText(slot.text)
    },
  })

  // ─── Phase 20: generate tailored feeling options on mount ───────────────────
  useEffect(() => {
    callClaude({
      phase: 20, emotion, intensity,
      messages: [{ role: 'user', content: '[Generate options]' }],
      firstName, sessionId,
      optionContext: `Question: "What are you feeling in this situation?" Emotion: ${emotion} (intensity ${intensity}/10). Generate 9 specific, nuanced facets or shades of "${emotion}" this person might actually be feeling — not top-level emotion categories, but the specific textures within this emotion. For example, for "grief": "Grief for what could have been", "The kind of sadness that hits in the middle of the night", "Grief laced with guilt". VARY the structure: some name the flavour, some name the trigger, some name where it lives. Each option must feel specific and true — the kind of feeling someone would read and think "yes, exactly that".`,
    }).then(result => {
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        setFeelingOptions(parsed.length >= 7 ? parsed : feelingFallback)
      } else {
        setFeelingOptions(feelingFallback)
      }
      setOptionsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Phase 2: fetch the body/memory question when opening ──────────────────
  useEffect(() => {
    if (uiState !== 'opening') return
    let cancelled = false
    callClaude({
      phase: 2, emotion, intensity,
      messages: [{ role: 'user', content: '[Begin phase]' }],
      firstName, sessionId, lifePathNumber, seriesContext,
    }).then(result => {
      if (cancelled) return
      if ('error' in result) { setErrorCode(errorCodeOf(result)); setUiState('error'); return }
      setClaudeQuestion(result.response)
      setUiState('awaiting-input')
    })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState, retryCount])

  // Auto-focus body textarea
  useEffect(() => {
    if (uiState === 'awaiting-input') bodyRef.current?.focus()
  }, [uiState])

  // Smooth scroll on new Claude content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [claudeQuestion, claudeReflection])

  // ─── Phase 20: generate tailored belief options while user does somatic step ─
  // Fires in background — belief options are usually ready by the time user reaches meaning-input.
  useEffect(() => {
    if (uiState !== 'somatic') return
    // Back and forward through this screen must not ask again for options built
    // from the same answers; changed answers get fresh ones.
    const basis = `${bodyLocation}\n${reminderText}`
    if (beliefOptionsFor.current === basis) return
    beliefOptionsFor.current = basis
    setBeliefOptionsLoading(true)
    const lpNote = lifePathNumber
      ? ` Their life path number is ${lifePathNumber} — subtly include 1-2 options that speak to that path's shadow.`
      : ''
    callClaude({
      phase: 20, emotion, intensity,
      messages: [{ role: 'user', content: '[Generate belief options]' }],
      firstName, sessionId, lifePathNumber,
      optionContext: `The person is working with "${emotion}" (intensity ${intensity}/10). They located this feeling in: ${bodyLocation}. ${reminderText ? `An early memory came up: "${reminderText}".` : 'No early memory came up.'} The question: "What did this experience make you believe about yourself?" Generate 7-9 short, first-person limiting beliefs drawn directly from this emotion + body location + memory combination. These should be raw and honest — the kind of thing someone believes but rarely says aloud. VARY the structure: some are about self-worth, some about safety, some about relationships, some about deserving. Every belief must feel like it could have formed from this specific experience — not a generic self-help category.${lpNote}`,
    }).then(result => {
      if (beliefOptionsFor.current !== basis) return  // answers changed meanwhile
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        setBeliefOptions(parsed.length >= 7 ? parsed : beliefFallback)
      } else {
        setBeliefOptions(beliefFallback)
      }
      setBeliefOptionsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState])

  // Seed messages with story context so Claude has full session history
  const feelingContext = intuitiveFeeling ? `They identified these feelings: ${intuitiveFeeling}.` : ''
  const seedMessages: SessionMessage[] = [
    { role: 'user', content: storyText + (feelingContext ? `\n\n${feelingContext}` : '') },
    { role: 'assistant', content: '[Story phase complete. Now exploring the body and memory.]' },
  ]

  // ─── RAIN: handle a single step's user submit ────────────────────────────
  // Stores the user's input under the current step letter, then fetches
  // Claude's prompt for the next step (or, on the final step, the nurturing
  // reflection). Each call is gated behind sessionMode === 'rain'.
  async function handleRainSubmit(currentStep: 'R' | 'A' | 'I') {
    const value = rainInput.trim()
    if (!value) return

    // Determine the next-step tag and screen
    const nextTag =
      currentStep === 'R' ? '[RAIN-A]' :
      currentStep === 'A' ? '[RAIN-I]' :
      '[RAIN-N]'
    const nextState: UIState =
      currentStep === 'R' ? 'rain-allow' :
      currentStep === 'A' ? 'rain-investigate' :
      'rain-nurture'
    const nextLetter = currentStep === 'R' ? 'A' : currentStep === 'A' ? 'I' : 'N'
    const laterLetters = ['R', 'A', 'I', 'N'].slice(['R', 'A', 'I', 'N'].indexOf(currentStep) + 1)

    // Back can bring the person to a step they have already answered. With the
    // same answer, the next step is already written: show it, with the answer
    // they gave there, and do not ask the guide again.
    if (rainResponses[currentStep] === value && rainTexts[nextLetter] !== undefined) {
      setRainInput(rainResponses[nextLetter] ?? '')
      setRainClaudeText(rainTexts[nextLetter])
      setUiState(nextState)
      return
    }

    // Snapshot what we now know — used both for state update and the Claude
    // call below, since setState is async and the call needs synchronous data.
    // A changed answer makes every later step stale (the guide wrote them from
    // the old one), so they are dropped here and asked for afresh as the person
    // goes on.
    const updatedResponses = { ...rainResponses, [currentStep]: value }
    for (const later of laterLetters) delete updatedResponses[later]
    setRainResponses(updatedResponses)
    setRainTexts(prev => {
      const kept = { ...prev }
      for (const later of laterLetters) delete kept[later]
      return kept
    })
    setRainInput('')
    setRainLoading(true)

    // Build conversation history — accumulates each prior RAIN exchange so
    // Claude has full somatic context for the nurture step.
    const msgs: SessionMessage[] = [
      { role: 'user', content: storyText },
      { role: 'assistant', content: '[Story phase complete. Now in RAIN.]' },
    ]
    if (updatedResponses.R) msgs.push({ role: 'user', content: `[RAIN-R answer] ${updatedResponses.R}` })
    if (updatedResponses.A) msgs.push({ role: 'user', content: `[RAIN-A answer] ${updatedResponses.A}` })
    if (updatedResponses.I) msgs.push({ role: 'user', content: `[RAIN-I answer] ${updatedResponses.I}` })
    msgs.push({ role: 'user', content: nextTag })

    const result = await callClaude({
      phase: 2, emotion, intensity, messages: msgs,
      firstName, sessionId, lifePathNumber, seriesContext,
      sessionMode: 'rain',
    })

    // Graceful fallback on error — keep the journey flowing
    const nextText = 'error' in result
      ? (currentStep === 'I' ? t('feel.rainInvestigateNote') : t('feel.rainAllowNote'))
      : result.response
    setRainClaudeText(nextText)
    setRainTexts(prev => ({ ...prev, [nextLetter]: nextText }))
    setRainLoading(false)
    setUiState(nextState)
  }

  // ─── Submit body + memory ────────────────────────────────────────────────────
  // Only moves on. The guide's reflection is NOT asked for here: it reflects the
  // whole of Feel, so it is asked for when the person leaves the expression
  // screen (requestReflection), once everything it should reflect is written.
  function handleSubmit() {
    const canSubmit = bodyLocation.trim() && (noMemory || reminderText.trim())
    if (!canSubmit) return
    setUiState('somatic')
  }

  // ─── The reflection (2.6) ──────────────────────────────────────────────────
  // Asked for when the person leaves 2.5, by Continue or by Skip. The guide is
  // given the body location and the memory (or that none came up) as before,
  // plus whichever of the body sensations, the belief and the expression the
  // person actually wrote: anything skipped is simply absent.
  //
  // `expression` is passed in because Skip clears it in the same click, and this
  // function would otherwise read the old value from this render.
  function requestReflection(expression: string) {
    const effectiveReminder = noMemory ? '[no early memory came up]' : reminderText.trim()
    const userResponse = `${bodyLocation.trim()}\n\n${effectiveReminder}`
    const sensations = [somaticChips.join(', '), somaticNote.trim()].filter(Boolean).join('. ')
    const details = {
      ...(sensations ? { sensations } : {}),
      ...(beliefText.trim() ? { belief: beliefText.trim() } : {}),
      ...(expression.trim() ? { expression: expression.trim() } : {}),
    }

    // Back can bring the person through here again after the guide has already
    // reflected on exactly these answers: show that reflection. If any of the
    // inputs changed, ask again, and clear the old reflection so 2.6 waits for the
    // new one instead of showing a stale one.
    const asked = JSON.stringify([userResponse, details])
    if (reflectionFor.current === asked) {
      setUiState('reflected')
      return
    }
    reflectionFor.current = asked
    setClaudeReflection(null)

    const msgs: SessionMessage[] = [
      ...seedMessages,
      { role: 'assistant', content: claudeQuestion! },
      { role: 'user', content: userResponse },
    ]

    callClaude({
      phase: 2, emotion, intensity, messages: msgs, firstName, sessionId, lifePathNumber, seriesContext,
      ...(Object.keys(details).length > 0 ? { feelDetails: details } : {}),
    }).then(result => {
      if (reflectionFor.current !== asked) return  // answers changed meanwhile
      if ('error' in result) {
        // Graceful fallback — keep the journey flowing; reflection is a gift, not a gate
        setClaudeReflection(t('feel.reflectionFallback'))
      } else {
        setClaudeReflection(result.response)
      }
    })

    setUiState('reflected')
  }

  const canSubmit = bodyLocation.trim() && (noMemory || reminderText.trim())

  // ─── Error state ─────────────────────────────────────────────────────────────
  if (uiState === 'error') {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 page-enter">
        <div className="max-w-sm w-full text-center">
          <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
            {t('errors.somethingWrong')}
          </p>
          <p className="text-sm mb-4" style={{ color: '#6B5F7A' }}>{t(guideErrorKey(errorCode))}</p>
          <button
            onClick={() => { setUiState('opening'); setErrorCode(null); setRetryCount(c => c + 1) }}
            style={{ background: '#3D1F62', color: 'white', border: 'none', borderRadius: '9999px', padding: '0.75rem 1.5rem', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            {t('errors.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  // ─── RAIN flow — gated entirely behind sessionMode === 'rain' ────────────
  // Each step uses the Feel teal accent (#3A9B7A) for continuity with the
  // standard somatic theme. The Recognize step uses a static prompt; A/I/N
  // each show Claude's response (rainClaudeText) and advance via Continue.
  if (
    uiState === 'rain-recognize' ||
    uiState === 'rain-allow' ||
    uiState === 'rain-investigate' ||
    uiState === 'rain-nurture'
  ) {
    // Map each UI state to its step letter and the prompt to display
    const step: 'R' | 'A' | 'I' | 'N' =
      uiState === 'rain-recognize' ? 'R' :
      uiState === 'rain-allow'     ? 'A' :
      uiState === 'rain-investigate' ? 'I' :
      'N'

    // The Recognize step uses static text (no Claude call yet). Steps A/I/N
    // use whatever Claude sent in rainClaudeText.
    const promptText = step === 'R' ? rainOpeningText : rainClaudeText

    // Map each step to the user-input handler; the Nurture step has no input,
    // it just shows Claude's reflection and a Continue button.
    const onContinue = () => {
      if (step === 'N') {
        // Final RAIN step → emit onComplete with mapped fields.
        // Mapping rationale (commented in state declaration above):
        //   bodyLocation = R answer (where they feel it)
        //   reminderText = I answer (what part of them needs / is protecting)
        //   beliefText   = A answer (felt experience under the allow)
        //   expressionText = I answer (investigative depth = honest expression)
        onComplete(
          rainResponses.R || '',
          rainResponses.I || '',
          rainResponses.A || '',
          rainResponses.I || '',
          '',  // the age question is for the standard lens only
        )
        return
      }
      handleRainSubmit(step)
    }

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          {/* Step supertitle — Feel teal accent */}
          <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#3A9B7A' }}>
            {rainLabels[step]}
          </p>

          {/* Question / reflection text. Loading spiral while fetching the next prompt. */}
          {rainLoading ? (
            <div className="mb-8">
              <LoadingSpiral size={22} />
            </div>
          ) : (
            promptText && (
              <div className="mb-8 message-appear">
                <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
                  {promptText}
                </p>
              </div>
            )
          )}

          {/* Input textarea — hidden on Nurture (final step is read-only) */}
          {step !== 'N' && !rainLoading && (
            <div className="rounded-2xl px-5 py-5 message-appear" style={{
              background: 'rgba(58,155,122,0.04)',
              borderTop: '3px solid #3A9B7A',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#3A9B7A' }}>
                {t('feel.yourResponse')}
              </p>
              <textarea
                value={rainInput}
                onChange={e => {
                  setRainInput(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = `${e.target.scrollHeight}px`
                }}
                placeholder={t('feel.takeYourTime')}
                maxLength={2000}
                autoFocus
                className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed transition-all focus:outline-none"
                style={{ borderColor: '#3A9B7A', background: 'white', color: '#1C1128', resize: 'none', overflow: 'hidden', minHeight: '160px' }}
                onFocus={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = '0 0 0 3px rgba(58,155,122,0.18)' }}
                onBlur={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = 'none' }}
              />
            </div>
          )}

          <div className="flex-1" />

          {/* Continue button — gated by either rainInput (R/A/I) or always-enabled on N */}
          <button
            onClick={onContinue}
            disabled={step !== 'N' && (rainLoading || !rainInput.trim())}
            className="w-full py-4 rounded-xl font-medium tracking-wide transition-all"
            style={{
              background: (step === 'N' || (!rainLoading && rainInput.trim())) ? '#3D1F62' : '#E4D5EE',
              color: (step === 'N' || (!rainLoading && rainInput.trim())) ? 'white' : '#9B8FAA',
              border: 'none',
              cursor: (step === 'N' || (!rainLoading && rainInput.trim())) ? 'pointer' : 'not-allowed',
            }}
          >
            {t('session.continue')}
          </button>
        </div>
      </div>
    )
  }

  // ─── Screen 2.1: Intuitive feeling picker ─────────────────────────────────
  if (uiState === 'intuitive-feeling') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
          <IntuitiveChoice
            initialState={feelingPick}
            onStateChange={setFeelingPick}
            prompt={t('feel.feelingQuestion')}
            options={feelingOptions}
            isLoadingOptions={optionsLoading}
            placeholder={t('feel.orDescribe')}
            ctaLabel={t('session.continue')}
            skipLabel={t('feel.skipContinue')}
            onComplete={value => {
              setIntuitiveFeeling(value)
              // The body question does not depend on the feeling chosen, so if
              // it has been asked already (the person went back to change the
              // feeling) it is not asked again.
              setUiState(claudeQuestion ? 'awaiting-input' : 'opening')
            }}
          />
        </div>
      </div>
    )
  }

  // ─── Screen 2.2: Body location + early memory ─────────────────────────────
  if (uiState === 'opening' || uiState === 'awaiting-input') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          {/* Claude's Phase 2 question */}
          {claudeQuestion && (
            <div className="mb-8 message-appear">
              <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
                {claudeQuestion}
              </p>
            </div>
          )}

          {/* Loading while fetching the question */}
          {uiState === 'opening' && (
            <div className="mb-8">
              <LoadingSpiral size={22} />
            </div>
          )}

          {/* Body + memory inputs */}
          {uiState === 'awaiting-input' && (
            <div className="flex-1 flex flex-col gap-5 message-appear">

              {/* Body location — wrapped in Feel teal card (#3A9B7A) at 4% tint */}
              <div className="rounded-2xl px-5 py-5" style={{
                background: 'rgba(58,155,122,0.04)',
                borderTop: '3px solid #3A9B7A',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#3A9B7A' }}>
                  {t('feel.bodyQuestion')}
                </p>

                {/* Body reference helper — expandable guide for people struggling to locate it */}
                <div className="mb-3">
                  <button
                    onClick={() => setShowBodyRef(v => !v)}
                    className="text-xs flex items-center gap-1"
                    style={{ background: 'none', border: 'none', color: '#9B8FAA', cursor: 'pointer', padding: 0 }}
                  >
                    <span style={{ fontSize: '0.65rem', transform: showBodyRef ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform 0.2s' }}>▶</span>
                    {showBodyRef ? t('feel.hideGuide') : t('feel.notSureWhere')}
                  </button>
                  {showBodyRef && (
                    <div
                      className="mt-2 px-4 py-3 rounded-xl text-xs leading-relaxed page-enter"
                      style={{ background: 'rgba(58,155,122,0.06)', border: '1.5px solid #C0E4DA', color: '#3D1F62' }}
                    >
                      <p className="font-medium mb-2" style={{ color: '#2A7A5A' }}>{t('feel.commonPlaces')}</p>
                      <ul className="space-y-1" style={{ paddingLeft: '1rem', listStyleType: 'disc' }}>
                        {bodyHelperList.map((line, i) => {
                          // "Place — meaning": bold the place, keep the rest plain.
                          const [place, ...rest] = line.split(' — ')
                          return (
                            <li key={i}>
                              <strong>{place}</strong>{rest.length > 0 && ` — ${rest.join(' — ')}`}
                            </li>
                          )
                        })}
                      </ul>
                      <p className="mt-2 italic" style={{ color: '#6B5F7A' }}>{t('feel.bodyHelperNote')}</p>
                    </div>
                  )}
                </div>

                {/* Chakra stacked selector */}
                <div className="flex flex-col items-center gap-1.5 mb-3">
                  {chakraList.map(loc => {
                    const isSelected = bodyLocation.toLowerCase().includes(loc.name.toLowerCase())
                    return (
                      <button
                        key={loc.name}
                        onClick={() => {
                          if (isSelected) {
                            setBodyLocation(prev =>
                              prev
                                .replace(new RegExp(`,?\\s*${loc.name}\\s*,?`, 'i'), '')
                                .replace(/^,\s*/, '')
                                .replace(/,\s*$/, '')
                                .trim()
                            )
                          } else {
                            setBodyLocation(prev => prev ? `${prev}, ${loc.name}` : loc.name)
                          }
                        }}
                        className="py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
                        style={{
                          width: '180px',
                          background: isSelected ? '#3D1F62' : 'white',
                          border: `1.5px solid ${isSelected ? '#3D1F62' : '#C4B5D4'}`,
                          color: isSelected ? 'white' : '#1C1128',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: loc.color, flexShrink: 0, display: 'inline-block' }} />
                        {loc.name}
                      </button>
                    )
                  })}
                </div>

                <textarea
                  ref={bodyRef}
                  value={bodyLocation}
                  onChange={e => setBodyLocation(e.target.value)}
                  placeholder={t('feel.orDescribe')}
                  rows={2}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none transition-all focus:outline-none"
                  // Border is always Feel phase accent teal
                  style={{ borderColor: '#3A9B7A', background: 'white', color: '#1C1128' }}
                  onFocus={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = '0 0 0 3px rgba(58,155,122,0.18)' }}
                  onBlur={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = 'none' }}
                />
              </div>

              {/* Early memory — wrapped in Feel teal card (#3A9B7A) at 4% tint */}
              <div className="rounded-2xl px-5 py-5" style={{
                background: 'rgba(58,155,122,0.04)',
                borderTop: '3px solid #3A9B7A',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '1.5px solid #E4D5EE',
              }}>
                <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#3A9B7A' }}>
                  {t('feel.memoryQuestion')}
                </p>

                {!noMemory ? (
                  <>
                    {/* Auto-grow textarea: starts at 140px min, expands with content.
                        Mobile fix — early memory writing was too cramped on small screens. */}
                    <textarea
                      value={reminderText}
                      onChange={e => {
                        setReminderText(e.target.value)
                        e.target.style.height = 'auto'
                        e.target.style.height = `${e.target.scrollHeight}px`
                      }}
                      placeholder={t('feel.memoryPlaceholder')}
                      maxLength={1000}
                      className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed transition-all focus:outline-none"
                      // Border is always Feel phase accent teal
                      style={{ borderColor: '#3A9B7A', background: 'white', color: '#1C1128', resize: 'none', overflow: 'hidden', minHeight: '140px' }}
                      onFocus={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = '0 0 0 3px rgba(58,155,122,0.18)' }}
                      onBlur={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = 'none' }}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <button
                        onClick={() => { setNoMemory(true); setReminderText('') }}
                        className="text-xs self-start"
                        style={{ color: '#9B8FAA', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {t('feel.nothingComesToMind')}
                      </button>
                      {voice.supported && (
                        <button
                          type="button"
                          onClick={() => voice.isListening
                            ? voice.stop()
                            : voice.start(t => setReminderText(prev => prev ? `${prev} ${t}` : t))
                          }
                          className="flex items-center gap-1 text-xs"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: voice.isListening ? '#C45272' : '#9B8FAA' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                            <rect x="4" y="0.5" width="5" height="7" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
                            <path d="M1.5 6.5a5 5 0 0 0 10 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                            <line x1="6.5" y1="11.5" x2="6.5" y2="12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                          </svg>
                          {voice.isListening ? t('session.voice.listening') : t('session.voice.dictate')}
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-xl"
                    style={{
                      // Teal-tinted background — Feel phase accent at ~4% opacity
                      background: 'rgba(58,155,122,0.04)',
                      // Left-border accent recipe for small inline context cards
                      borderTop: '1.5px solid #E4D5EE',
                      borderRight: '1.5px solid #E4D5EE',
                      borderBottom: '1.5px solid #E4D5EE',
                      borderLeft: '3px solid #3A9B7A',
                    }}
                  >
                    <p className="text-sm" style={{ color: '#6B5F7A' }}>{t('feel.nothingCameUp')}</p>
                    <button
                      onClick={() => setNoMemory(false)}
                      className="text-xs ml-3 flex-shrink-0"
                      style={{ color: '#9B72C0', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {t('feel.actuallySomething')}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex-1" />

              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full py-4 rounded-xl font-medium tracking-wide transition-all"
                style={{
                  background: canSubmit ? '#3D1F62' : '#E4D5EE',
                  color: canSubmit ? 'white' : '#9B8FAA',
                  border: 'none',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {t('session.continue')}
              </button>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>
    )
  }

  // ─── Somatic Activation ───────────────────────────────────────────────────────
  // Belief options for the next step are being generated in background right now.
  if (uiState === 'somatic') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          <div className="mb-8">
            <p className="text-base leading-relaxed font-display italic mb-2" style={{ color: '#1C1128' }}>
              {t('feel.somaticHeading')}
            </p>
            <p className="text-base leading-relaxed font-display italic" style={{ color: '#1C1128' }}>
              {t('feel.somaticSubheading')}
            </p>
          </div>

          {/* Somatic chips */}
          <div className="flex flex-wrap gap-2 mb-5">
            {somaticChipList.map(chip => {
              const isSelected = somaticChips.includes(chip)
              return (
                <button
                  key={chip}
                  onClick={() => setSomaticChips(prev =>
                    isSelected ? prev.filter(c => c !== chip) : [...prev, chip]
                  )}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                  style={{
                    background: isSelected ? '#3D1F62' : 'white',
                    border: `1.5px solid ${isSelected ? '#3D1F62' : '#C4B5D4'}`,
                    color: isSelected ? 'white' : '#6B5F7A',
                    cursor: 'pointer',
                  }}
                >
                  {chip}
                </button>
              )
            })}
          </div>

          {/* Free text — wrapped in Feel teal card (#3A9B7A) at 4% tint */}
          <div className="rounded-2xl px-5 py-5 mb-4" style={{
            background: 'rgba(58,155,122,0.04)',
            borderTop: '3px solid #3A9B7A',
            borderRight: '1.5px solid #E4D5EE',
            borderBottom: '1.5px solid #E4D5EE',
            borderLeft: '1.5px solid #E4D5EE',
          }}>
            <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#3A9B7A' }}>
              {t('feel.whatShifts')}
            </p>
            <textarea
              value={somaticNote}
              onChange={e => setSomaticNote(e.target.value)}
              placeholder={t('feel.orDescribe')}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none transition-all focus:outline-none"
              // Border is always Feel phase accent teal
              style={{ borderColor: '#3A9B7A', background: 'white', color: '#1C1128' }}
              onFocus={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = '0 0 0 3px rgba(58,155,122,0.18)' }}
              onBlur={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = 'none' }}
            />
          </div>

          <div className="flex-1" />

          <button
            onClick={() => setUiState('meaning-input')}
            disabled={!somaticHasInput}
            className="w-full py-4 rounded-xl font-medium tracking-wide transition-all mb-2"
            style={{
              background: somaticHasInput ? '#3D1F62' : '#E4D5EE',
              color: somaticHasInput ? 'white' : '#9B8FAA',
              border: 'none',
              cursor: somaticHasInput ? 'pointer' : 'not-allowed',
            }}
          >
            {t('session.continue')}
          </button>
          <button
            onClick={() => { setSomaticChips([]); setSomaticNote(''); setUiState('meaning-input') }}
            className="w-full py-2 text-sm"
            style={{ background: 'none', border: 'none', color: '#9B8FAA', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {t('session.skip')}
          </button>

        </div>
      </div>
    )
  }

  // ─── Meaning Imprint ──────────────────────────────────────────────────────────
  if (uiState === 'meaning-input') {
    // What "this" refers to in the belief question — the felt experience the user
    // just described (where they feel it, and what it reminds them of), so the
    // question isn't asking them to respond to an off-screen referent.
    const sittingWith = bodyLocation.trim()
      ? t('feel.sittingWithBody', { location: bodyLocation.trim() })
      : t('session.emotionYouCameInWith', { emotion: localizeEmotionLabel(emotion, i18n.language) })
    const memoryClause = !noMemory && reminderText.trim()
      ? ` ${t('feel.remindsClause', { text: reminderText.trim() })}`
      : ''
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
          <div className="mb-6 px-4 py-3 rounded-xl" style={{
            background: 'rgba(58,155,122,0.04)',
            borderTop: '1.5px solid #E4D5EE',
            borderRight: '1.5px solid #E4D5EE',
            borderBottom: '1.5px solid #E4D5EE',
            borderLeft: '3px solid #3A9B7A',
          }}>
            <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('feel.whatYoureSittingWith')}</p>
            <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>
              {sittingWith}.{memoryClause}
            </p>
          </div>
          <IntuitiveChoice
            initialState={beliefPick}
            onStateChange={setBeliefPick}
            prompt={t('feel.beliefQuestion')}
            options={beliefOptions}
            isLoadingOptions={beliefOptionsLoading}
            placeholder={t('feel.orWriteOwn')}
            ctaLabel={t('session.continue')}
            skipLabel={t('session.skip')}
            onComplete={value => {
              setBeliefText(value)
              setUiState('expression')
            }}
          />
        </div>
      </div>
    )
  }

  // ─── Expression Step ──────────────────────────────────────────────────────────
  if (uiState === 'expression') {
    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          <div className="mb-6">
            <p className="text-base leading-relaxed font-display italic mb-3" style={{ color: '#1C1128' }}>
              {t('feel.expressionQuestion')}
            </p>
            <p className="text-xs" style={{ color: '#9B8FAA', fontStyle: 'italic' }}>
              {t('feel.expressionSubtitle')}
            </p>
          </div>

          {/* Expression textarea — wrapped in Feel teal card (#3A9B7A) at 4% tint */}
          <div className="rounded-2xl px-5 py-5" style={{
            background: 'rgba(58,155,122,0.04)',
            borderTop: '3px solid #3A9B7A',
            borderRight: '1.5px solid #E4D5EE',
            borderBottom: '1.5px solid #E4D5EE',
            borderLeft: '1.5px solid #E4D5EE',
          }}>
            <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#3A9B7A' }}>
              {t('feel.yourExpression')}
            </p>
            {/* Auto-grow expression textarea — uncensored writing often runs long.
                minHeight 180px gives mobile users comfortable writing room from the start. */}
            <textarea
              value={expressionText}
              onChange={e => {
                const val = e.target.value
                setExpressionText(val)
                if (!showCrisis && detectCrisis(val)) setShowCrisis(true)
                e.target.style.height = 'auto'
                e.target.style.height = `${e.target.scrollHeight}px`
              }}
              placeholder={t('feel.expressionPlaceholder')}
              maxLength={2000}
              className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed transition-all focus:outline-none"
              // Border is always Feel phase accent teal
              style={{ borderColor: '#3A9B7A', background: 'white', color: '#1C1128', resize: 'none', overflow: 'hidden', minHeight: '180px' }}
              onFocus={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = '0 0 0 3px rgba(58,155,122,0.18)' }}
              onBlur={e => { e.target.style.borderColor = '#3A9B7A'; e.target.style.boxShadow = 'none' }}
            />
          </div>

          {showCrisis && <CrisisAlert />}

          <div className="flex-1" />

          {voice.supported && (
            <button
              type="button"
              onClick={() => voice.isListening
                ? voice.stop()
                : voice.start(t => setExpressionText(prev => prev ? `${prev} ${t}` : t))
              }
              className="flex items-center gap-1 text-xs mt-3 self-end"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: voice.isListening ? '#C45272' : '#9B8FAA' }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <rect x="4" y="0.5" width="5" height="7" rx="2.5" stroke="currentColor" strokeWidth="1.2" />
                <path d="M1.5 6.5a5 5 0 0 0 10 0" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <line x1="6.5" y1="11.5" x2="6.5" y2="12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
              {voice.isListening ? t('session.voice.listening') : t('session.voice.dictate')}
            </button>
          )}

          <button
            onClick={() => requestReflection(expressionText)}
            disabled={!expressionText.trim()}
            className="w-full py-4 rounded-xl font-medium tracking-wide transition-all mt-6 mb-2"
            style={{
              background: expressionText.trim() ? '#3D1F62' : '#E4D5EE',
              color: expressionText.trim() ? 'white' : '#9B8FAA',
              border: 'none',
              cursor: expressionText.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            {t('session.continue')}
          </button>
          <button
            onClick={() => { setExpressionText(''); requestReflection('') }}
            className="w-full py-2 text-sm"
            style={{ background: 'none', border: 'none', color: '#9B8FAA', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {t('session.skip')}
          </button>

        </div>
      </div>
    )
  }

  // ─── Reflected: Claude's reflection (background Phase 2 call result) ──────────
  // Shows spinner if the background call hasn't completed yet.
  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        {claudeReflection === null ? (
          <LoadingSpiral size={22} label={t('session.generating')} />
        ) : (
          <div className="flex-1 flex flex-col message-appear">

            {/* Summary card — what the user shared */}
            <div
              className="mb-6 px-4 py-3 rounded-xl"
              style={{
                // Teal-tinted background — Feel phase accent at ~4% opacity
                background: 'rgba(58,155,122,0.04)',
                // Left-border accent recipe for small inline context cards
                borderTop: '1.5px solid #E4D5EE',
                borderRight: '1.5px solid #E4D5EE',
                borderBottom: '1.5px solid #E4D5EE',
                borderLeft: '3px solid #3A9B7A',
              }}
            >
              <p className="text-xs mb-1" style={{ color: '#6B5F7A' }}>{t('feel.bodyLabel')}</p>
              <p className="text-sm" style={{ color: '#3D1F62' }}>{bodyLocation}</p>
              <p className="text-xs mt-3 mb-1" style={{ color: '#6B5F7A' }}>{t('feel.remindsYouOf')}</p>
              <p className="text-sm leading-relaxed" style={{ color: '#3D1F62' }}>
                {noMemory ? t('feel.nothingCameUpShort') : reminderText}
              </p>
            </div>

            {/* Claude's reflection */}
            <p className="text-base leading-relaxed font-display italic mb-8" style={{ color: '#1C1128' }}>
              {claudeReflection}
            </p>

            {ageShown && (
              <div className="mb-8">
                <label htmlFor="feel-age" className="block text-sm mb-2" style={{ color: '#3D1F62' }}>
                  {t('feel.ageQuestion')}
                </label>
                <input
                  id="feel-age"
                  type="text"
                  value={ageText}
                  onChange={e => {
                    const val = e.target.value
                    setAgeText(val)
                    if (!ageCrisis && detectCrisis(val)) setAgeCrisis(true)
                  }}
                  placeholder={t('feel.agePlaceholder')}
                  maxLength={40}
                  className="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none"
                  style={{ borderColor: '#3A9B7A', background: 'white', color: '#1C1128' }}
                  onFocus={e => { e.target.style.boxShadow = '0 0 0 3px rgba(58,155,122,0.18)' }}
                  onBlur={e => { e.target.style.boxShadow = 'none' }}
                />
                <p className="text-xs mt-2" style={{ color: '#9B8FAA' }}>{t('feel.ageHelper')}</p>
                {ageCrisis && <CrisisAlert />}
              </div>
            )}

            <div className="flex-1" />

            <button
              onClick={() => onComplete(bodyLocation, noMemory ? '' : reminderText, beliefText, expressionText, ageShown ? ageAnswer(ageText) : '')}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('session.continue')}
            </button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  )
}

